<script setup lang="ts">
import { computed, ref, } from 'vue';
import { useI18n } from 'vue-i18n';
import StatusBadge from '@/components/utilities/StatusBadge.vue';
import ImageCarousel from '@/components/utilities/ImageCarousel.vue';
import ImageLightbox from '@/components/utilities/ImageLightbox.vue';
import { formatCurrencyWithoutDecimal } from '@/helpers/currencyHelper.ts';
import { formatDateAsISO, formatAvailabilityDate } from '@/helpers/dateHelpers.ts';
import { getStyleThumbnailImageUrl } from '@/helpers/imageHelpers.ts';
import { formatNumberWithSeparators } from '@/helpers/numberHelpers.ts';
import type { LocaleSchema } from '@/locales/index.ts';
import type { Building } from '@/typings/buildings/Building.ts';
import type { QuoteOption } from '@/typings/quotes/QuoteOption.ts';
import type { NewQuoteInfo } from '@/typings/quotes/NewQuotesInfo.ts';
import type { Unit } from '@/typings/units/Unit.ts';
import type { UnitImage } from '@/typings/units/UnitImage.ts';
import type { UnitStyle } from '@/typings/units/UnitStyle.ts';
import type { UnitStyleImage } from '@/typings/units/UnitStyleImage.ts';
import BedFront from '@/components/svg/BedFront.vue';
import Ruler from '@/components/svg/Ruler.vue';
import Bath from '@/components/svg/Bath.vue';
import Floor from '@/components/svg/Floor.vue';
import ChargingStation from '@/components/svg/ChargingStation.vue';
import { getUnitPositionText } from '@/enums/UnitPosition';
import { getUnitStatusString, UnitStatusEnum } from '@/enums/UnitStatus';

const { t, locale } = useI18n<{message: LocaleSchema}>({
  useScope: 'global',
});

const props = defineProps<{
  unit: Unit;
  building: Building;
  unitStyles: UnitStyle[];
  options: QuoteOption[];
  quoteInfo: NewQuoteInfo;
}>();

const emit = defineEmits<{
  'update:selectedConfigurationId': [value: number | undefined];
  'update:selectedStyleId': [value: number | undefined];
}>();

const optionsHeading = computed(() => {
  return props.unit.hasDefaultOptions ? `${t('unitSummary.additionalOptions')} ( ${t('unitSummary.preConfigured_F', 2).toLocaleLowerCase()} )` : t('unitSummary.additionalOptions');
});

const filteredStyles = computed(() => {
  // For rental buildings: use only the unit's specific unitStyle (first one from unitStyles array)
  // If unit.unitStyles is null/empty, don't show unit styles section
  if (props.unit.unitStyles && props.unit.unitStyles.length > 0) {
    // Take only the first unit style (specific to this rental unit)
    const firstUnitStyle = props.unit.unitStyles[0];
    if (firstUnitStyle && !firstUnitStyle.isSystem) {
      return [firstUnitStyle];
    }
  }

  return [];
});

const selectedStyleId = ref<number | null>(
  filteredStyles.value.length > 0 ? filteredStyles.value[0].id : null
);

const selectedStyle = computed(() => {
  if (!selectedStyleId.value) return null;
  return filteredStyles.value.find(style => style.id === selectedStyleId.value) || null;
});

const selectedStyleImages = computed(() => {
  if (!selectedStyle.value?.images) return [];
  
  const localeValue = locale.value.toLowerCase();
  
  return [...selectedStyle.value.images]
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
    .map((img: UnitStyleImage, index: number) => ({
      imageUrl: img.imageUrl,
      displayOrder: img.displayOrder,
      alt: `${selectedStyle.value?.localizedName} - Image ${index + 1}`,
      caption: localeValue === 'en-ca' ? img.nameEn : img.name
    }));
});

const description = computed(() => {
  const localeMapping: Record<string, string> = {
    'fr-ca': 'description',
    'en-ca': 'descriptionEn'
  };

  const localeValue = locale.value.toLowerCase();

  return props.unit[localeMapping[localeValue]];
});

const unitImages = computed(() => {
  const images = props.unit.images as UnitImage[] | undefined;
  if (!images?.length) return [];
  
  const localeValue = locale.value.toLowerCase();
  
  return images.map((img: UnitImage) => ({
    imageUrl: img.imageUrl,
    displayOrder: img.displayOrder,
    alt: `Image ${img.displayOrder || 1} - ${t('unitSummary.unit')} ${props.unit.unitNumber}`,
    caption: localeValue === 'en-ca' ? img.nameEn : img.name
  }));
});

const getOptionPrice = (option: QuoteOption): string => {
  return formatCurrencyWithoutDecimal(props.unit.hasDefaultOptions ? 0 : option.price, locale);
};

function getStyleThumbnailImage(imageUrl: string): string {
  return getStyleThumbnailImageUrl(imageUrl, 300);
}

// Configurations disponibles avec logique d'héritage
const availableConfigurations = computed(() => {
  const isEnglish = locale.value.toLowerCase().includes('en');
  const configs: Array<{value: number; label: string; systemKey: string; amount?: number | null}> = [];
  
  // Utiliser unitUnitConfigurations pour obtenir les configurations disponibles
  if (props.unit.unitUnitConfigurations && props.unit.unitUnitConfigurations.length > 0) {
    props.unit.unitUnitConfigurations.forEach(config => {
      const label = isEnglish
        ? (config.unitConfigurationNameEn || config.unitConfigurationName || '')
        : (config.unitConfigurationName || '');
      
      if (label) {
        configs.push({
          value: config.unitConfigurationId,
          label: label,
          systemKey: config.unitConfigurationSystemKey || '',
          amount: config.amount
        });
      }
    });
  }
  
  return configs;
});

const selectedConfigurationId = ref<number | null>(
  availableConfigurations.value.length > 0 ? availableConfigurations.value[0].value : null
);

const selectedConfiguration = computed(() => {
  return availableConfigurations.value.find(c => c.value === selectedConfigurationId.value);
});

const displayConfigurationName = computed(() => {
  // Si une configuration est sélectionnée, l'utiliser
  if (selectedConfiguration.value) {
    let displayName = selectedConfiguration.value.label;
    if (selectedConfiguration.value.amount) {
      displayName += ` (+${formatCurrencyWithoutDecimal(selectedConfiguration.value.amount, locale)})`;
    }
    return displayName;
  }
  
  // Sinon, utiliser la première configuration disponible
  if (availableConfigurations.value.length > 0) {
    const firstConfig = availableConfigurations.value[0];
    let displayName = firstConfig.label;
    if (firstConfig.amount) {
      displayName += ` (+${formatCurrencyWithoutDecimal(firstConfig.amount, locale)})`;
    }
    return displayName;
  }
  
  return 'n.d.';
});

const floorPlanImage = computed(() => {
  if (!selectedConfiguration.value) return null;
  
  // Utiliser unitUnitConfigurations pour trouver l'image
  const configImage = props.unit.unitUnitConfigurations?.find(
    ci => ci.unitConfigurationId === selectedConfiguration.value?.value
  );
  
  return configImage?.planImageUrl || null;
});

function onStyleChange(event: Event) {
  const target = event.target as HTMLSelectElement;
  selectedStyleId.value = target.value ? parseInt(target.value) : null;
}

function onConfigurationChange(configId: number) {
  selectedConfigurationId.value = configId;
  emit('update:selectedConfigurationId', selectedConfigurationId.value);
}

function onStyleChangeHandler(event: Event) {
  onStyleChange(event);
  emit('update:selectedStyleId', selectedStyleId.value ?? undefined);
}

const humanReadableAvailabilityDate = computed(() => {
  if (!props.unit.availabilityDate) return '';
  
  return formatAvailabilityDate(props.unit.availabilityDate, locale.value.toLowerCase(), t, 'rental', props.unit.status);
});

const floorName = computed(() => {
  const localeMapping: Record<string, string> = {
    'fr-ca': props.unit.floorName != null ? props.unit.floorName : t('common.notAvailableAbbreviated'),
    'en-ca': props.unit.floorNameEn != null ? props.unit.floorNameEn : t('common.notAvailableAbbreviated')
  };

  const localeValue = locale.value.toLowerCase();

  return localeMapping[localeValue];
});

const unitPositionText = computed(() => getUnitPositionText(props.unit.position, t));

const shouldShowAvailabilityDate = computed(() => {
  return props.unit.availabilityDate && props.unit.status === UnitStatusEnum.Available;
});

// Emit initial values
emit('update:selectedConfigurationId', selectedConfigurationId.value ?? undefined);
emit('update:selectedStyleId', selectedStyleId.value ?? undefined);
</script>

<template>
  <article class="unitSummary-details">
    <!--Entête Nom Unité-->
    <header class="unitSummary-header">
      <h1>
        <span>{{ t('unitSummary.unit') }} {{ unit.unitNumber }}</span>
      </h1>
      <StatusBadge class="unitSummary-statusBadge"
                   :status="getUnitStatusString(unit.status)"
                   :label="t(`browsingApp.statuses.unitStatus.${getUnitStatusString(unit.status)}`)" />
    </header>
    <!-- Unit Specifications -->
    <section class="unitSummary-specifications">
      <div class="unitSummary-specItem">
        <div class="icon">
          <Floor></Floor>
        </div>
        <span class="unitSummary-specText">{{ floorName }}</span>
      </div>
      <div class="unitSummary-specItem">
        <div class="icon">
          <BedFront></BedFront>
        </div>
        <span class="unitSummary-specText">{{ displayConfigurationName }}</span>
      </div>
      <div class="unitSummary-specItem" v-if="unit.livingArea">
        <div class="icon">
          <Ruler></Ruler>
        </div>
        <span class="unitSummary-specText">{{ unit.livingArea ? formatNumberWithSeparators(unit.livingArea, locale) : '' }} pi²</span>
      </div>
      <div class="unitSummary-specItem" v-if="unit.hasChargingStation != null">
        <div class="icon">
          <ChargingStation></ChargingStation>          
        </div>
        <span class="unitSummary-specText">{{ t('unitSummary.chargingStation') }}</span>
      </div>
    </section>
    <!-- Configuration Selection -->
    <section class="unitSummary-features" v-if="availableConfigurations.length > 1">
      <div class="unitSummary-featuresTitle">{{ t('unitSummary.chooseConfiguration') }}</div>
      <div class="unitSummary-configSelector">
        <button
          v-for="config in availableConfigurations"
          :key="config.value"
          class="config-button"
          :class="{ 'config-button_active': selectedConfigurationId === config.value }"
          @click="onConfigurationChange(config.value)"
          type="button"
        >
          {{ config.label }}
          <span v-if="config.amount" class="config-amount">+{{ formatCurrencyWithoutDecimal(config.amount, locale) }}</span>
        </button>
      </div>
    </section>
    <!-- Date d'emménagement -->
    <section class="unitSummary-features" v-if="shouldShowAvailabilityDate">
      <div class="unitSummary-featuresTitle">{{ t('unitSummary.moveInDate') }}</div>
      <div>{{ humanReadableAvailabilityDate }}</div>
    </section>
    <!-- Position -->
    <section class="unitSummary-features" v-if="unitPositionText">
      <div class="unitSummary-featuresTitle">{{ t('browsingApp.units.position') }}</div>
      <div>{{ unitPositionText }}</div>
    </section>
    <!-- Description -->
    <section class="unitSummary-features" v-if="description">
      <div class="unitSummary-featuresTitle">{{ t('unitSummary.description') }}</div>
      <div class="unitSummary-descriptionText" v-html="description"></div>
      <p class="unitSummary-petPolicy" v-if="!unit.dogAllowed">{{ t('unitSummary.petPolicy') }}</p>
    </section>
    <!-- Unit Images -->
    <section class="unitSummary-features" v-if="unitImages && unitImages.length > 0">
      <div class="unitSummary-featuresTitle">{{ t('unitSummary.images') }}</div>
      <ImageCarousel :images="unitImages"
                     :show-lightbox="true"
                     :show-dots="true"
                     :show-arrows="true"
                     aspect-ratio="16/9"
                     :image-width="600"
                     class="unitSummary-imageCarousel" />
    </section>
    <!-- Unit Styles Section -->
    <section class="unitSummary-features" v-if="filteredStyles.length > 0">
      <div class="unitSummary-featuresTitle">{{ t('unitSummary.style') }}</div>

      <!-- Dropdown de sélection de style -->
      <div class="unitSummary-styleSelector" v-if="filteredStyles.length > 1">
        <select :value="selectedStyleId"
                @change="onStyleChangeHandler"
                class="unitSummary-styleSelect">
          <option v-for="style in filteredStyles"
                  :key="style.id"
                  :value="style.id">
            {{ style.localizedName }}
          </option>
        </select>
      </div>
      
      <!-- Titre du style sélectionné si un seul style -->
      <div v-else-if="selectedStyle" class="unitSummary-selectedStyleTitle">
        <span>{{ selectedStyle.localizedName }}</span>
      </div>

      <!-- Grille d'images du style sélectionné -->
      <div v-if="selectedStyleImages.length > 0" class="unitSummary-styleImagesGrid">
        <div v-for="image in selectedStyleImages"
              :key="image.displayOrder"
              class="unitSummary-styleImageItem">
          <figure class="unitSummary-styleImageFigure">
            <ImageLightbox :display-url="getStyleThumbnailImage(image.imageUrl)"
                            :modal-url="image.imageUrl"
                            :alt="image.alt" />
            <figcaption v-if="image.caption"
                        class="unitSummary-styleImageCaption">
              {{ image.caption }}
            </figcaption>
          </figure>
        </div>
      </div>
    </section>
  </article>
</template>

<style lang="scss">
@import '@/assets/styles/components/unitSummaryDetails.scss';

/* Styles spécifiques au composant Rental */

/* Styles spécifiques Rental - Header */
.unitSummary-header {
  display: flex;
  justify-content: space-between;

  h1 {
    font-family: "Inter", Arial, Helvetica, sans-serif;
    display: flex;
    align-items: center;
    gap: 2.8rem;
    margin: 0;
    font-size: 2.0rem;
    font-weight: 800;
    color: var(--gray-700);
  }
}

/* Styles spécifiques Rental - Specifications */
.unitSummary-specifications {
  display: flex;
  flex-wrap: wrap;
  gap: 2.4rem;
  color: var(--gray-700);
  margin-top: 2.4rem;

  .unitSummary-specItem {
    display: flex;
    align-items: center;
    gap: 1.0rem;
    font-size: 1.6rem;

    .icon {
      width: 1.6rem;
      height: 1.6rem;
      flex-shrink: 0;
      color: var(--gray-700);

      svg {
        width: 100%;
        height: 100%;
      }
    }

    .unitSummary-specText {
      font-weight: 500;
    }
  }
}

/* Styles spécifiques Rental - Features */
.unitSummary-features {
  margin-top: 2.4rem;
  padding-top: 2.4rem;
  font-size: 1.6rem;
  font-weight: 700;
  border-top: 2px solid var(--contentColorBorder);

  .unitSummary-featuresTitle {
    font-family: "Inter", Arial, Helvetica, sans-serif;
    margin: 0;
    font-size: 1.4rem;
    font-weight: 700;
    color: var(--gray-500);

    & + .unitSummary-imageCarousel{
      margin-top: 1.0rem;
    }
  }

  .unitSummary-featuresList {
    list-style: none;
    padding: 0;
    margin-inline: 0;

    li {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      gap: 16px;
      padding: 8px 12px;

      &:before{
        display: none;
      }

      &:nth-child(even){
        background-color: var(--gray-50);
        border-radius: 4px;
      }
    }
  }

  .unitSummary-descriptionText {
    margin: 0;
    line-height: 1.5;
    font-weight: 400;
  }

  .unitSummary-petPolicy{
    line-height: 1.5;
    font-weight: 400;
  }
}

.config-amount {
  font-size: 0.9em;
  color: var(--gray-600);
  font-weight: 500;
  margin-left: 0.5rem;
}

/* Styles spécifiques Rental - Media Queries */
@media screen and (max-width: 768px) {
  .unitSummary-details {
    gap: 1.6rem;
    flex-direction: column;
  }
}
</style>
